import request from '@/api/models/request';
import {
  API_METHODS,
  ERROR_CODE,
  ERROR_CODE_NAME,
  TIME,
} from '@/const';

const NOT_TRACKED_STATUS_CODES = [ERROR_CODE.UNAUTHENTICATED, ERROR_CODE.NOT_FOUND];
const NOT_TRACKED_ERROR_CODES = [ERROR_CODE_NAME.NETWORK];

/**
 * Class representing a SiteTracker.
 * This class is responsible for sending request error logs to Site24x7.
 */
class SiteTracker {
  #baseUrl = 'https://logc.site24x7.com';
  #url = '/event/receiver';
  #CHANNEL_NAME = process.env.VUE_APP_CHANNEL_CONFIG_NAME;
  #REQUEST_ERROR_LOG_TOKEN = process.env.VUE_APP_SITE24X7_REQUEST_ERROR_TOKEN;
  #CODE_ERROR_LOG_TOKEN = process.env.VUE_APP_SITE24X7_CODE_ERROR_TOKEN;
  #BUY_FLOW_LOG_TOKEN = process.env.VUE_APP_SITE24X7_BUY_FLOW_TOKEN;
  #BUILD = process.env.VUE_APP_BUILD_NUMBER;
  #sendTrackTimeout = {
    id: null,
    time: TIME.SECOND * 5,
  };
  #requestErrorQueue = new Map();
  #codeErrorQueue = new Map();
  #userId = null;
  #checkConnectionInterval = {
    id: null,
    time: TIME.SECOND,
  };
  
  /**
   * Set the user ID for Site24x7.
   * @param {String|Number|null} userId - The user ID to be set.
   */
  set userId(userId) {
    this.#userId = userId;
  }
  /**
   * Get the user ID for Site24x7.
   * @return {string} Returns the user ID.
   */
  get userId() {
    return this.#userId? String(this.#userId) : '';
  }
  
  /**
   * Get the internet connection status.
   * @return {boolean} Returns true if the internet connection is available, false otherwise.
   */
  get internetConnection() {
    return navigator.onLine;
  };
  
  /**
   * Check the internet connection status every 1 second.
   * If the internet connection is available, the queue is released.
   * If the internet connection is unavailable, the queue is not released.
   * @return {void}
   */
  internetConnectionCheck() {
    if (this.#checkConnectionInterval.id) return;
    
    this.#checkConnectionInterval.id = setInterval(() => {
      if (navigator.onLine) {
        this.releaseQueue();
        clearInterval(this.#checkConnectionInterval.id);
        this.#checkConnectionInterval.id = null;
      }
    }, this.#checkConnectionInterval.time);
  };
  
  /**
   * Release the queue of request and code errors.
   * @param {object} [event] - The event object (beforeunload).
   * @param {string} [event.type] - The type of the event.
   */
  releaseQueue = (event) => {
    const sendBeacon = event?.type === 'beforeunload';
    const requestList = [];
    
    if (this.#codeErrorQueue.size) {
      requestList.push({
        url: `${this.#url}?token=${this.#CODE_ERROR_LOG_TOKEN}`,
        baseUrl: this.#baseUrl,
        method: API_METHODS.POST,
        params: [...this.#codeErrorQueue.values()],
      });
      this.#codeErrorQueue.clear();
    }
    if (this.#requestErrorQueue.size) {
      requestList.push({
        url: `${this.#url}?token=${this.#REQUEST_ERROR_LOG_TOKEN}`,
        baseUrl: this.#baseUrl,
        method: API_METHODS.POST,
        params: [...this.#requestErrorQueue.values()],
      });
      this.#requestErrorQueue.clear();
    }
    
    requestList.forEach((request) => {
      sendBeacon? this.sendBeacon(request) : this.sendError(request);
    });
  };
  
  /**
   * Initialize the timeout to release the queue of request and code errors.
   * If the internet connection is available, the queue is released.
   * If the internet connection is unavailable, the internetConnectionCheck method runs.
   * @return {void}
   */
  initTimeout() {
    if (this.#sendTrackTimeout.id) return;
    
    this.#sendTrackTimeout.id = setTimeout(() => {
      if (this.internetConnection) {
        this.releaseQueue();
      } else {
        this.internetConnectionCheck();
      }
      
      clearInterval(this.#sendTrackTimeout.id);
      this.#sendTrackTimeout.id = null;
    }, this.#sendTrackTimeout.time);
  }
  
  /**
   * Add an error to the queue.
   * @param {object} err
   * @param {String} key
   * @param {Map} map
   */
  addError(err, key, map) {
    const exist = map.has(key);
    const params = exist? map.get(key) : err;
    exist && params.err_count++;
    map.set(key, params);
    
    this.initTimeout();
  }
  
  /**
   * Prepares the parameters for sending a request error log to Site24x7.
   * This method extracts and formats the necessary details from the error object
   * to create a structured log entry.
   *
   * @param {Object} err - The error object containing details of the request error.
   * @param {Object} err.config - The configuration of the request that caused the error.
   * @param {string} err.config.url - The URL of the request.
   * @param {string} err.config.baseURL - The base URL of the request.
   * @param {Object} [err.config.data] - The data sent with the request.
   * Can be a FormData object, string, or other types.
   * @param {Object} err.response - The response object of the failed request.
   * @param {number} err.response.status - The HTTP status code of the response.
   * @param {string} [err.response.data.message] - The error message from the response data.
   * @param {string} [err.response.statusText] - The status text of the response.
   * @param {string} err.code - The error code associated with the request failure.
   * @param {string} err.message - The error message, generated by Axios or the browser.
   *
   * @return {Object} A structured object containing the following fields:
   * - `date` {string}: The current timestamp in ISO format.
   * - `status` {number}: The HTTP status code of the response.
   * - `service` {string}: The service type, set to 'request'.
   * - `channel` {string}: The channel name, retrieved from the environment variable.
   * - `message` {string}: The error message, derived from the response or error object.
   * - `request_data` {string}: The serialized request data.
   * - `url` {string}: The full URL of the request.
   * - `code` {string}: The error code.
   * - `user_id` {string}: The user ID, if available.
   * - `err_count` {number}: The error count, initialized to 1.
   * - `userAgent` {string}: The user agent of the browser.
   */
  prepareParams(err) {
    const paramUrl = new URL(
        err?.config?.url || '[someUndefinedURL]',
        err?.config?.baseURL || location.origin,
    );
    let requestData;
    
    if (err?.config?.data instanceof FormData) {
      requestData = JSON.stringify(Object.fromEntries(err.config?.data.entries() || []));
    } else if (typeof err?.config?.data === 'string') {
      requestData = err?.config?.data;
    } else {
      requestData = JSON.stringify(err?.config?.data);
    }
    
    return {
      date: (new Date()).toISOString(),
      status: err?.response?.status,
      service: 'request',
      channel: this.#CHANNEL_NAME,
      message: err?.response?.data?.message || err?.response?.statusText || err?.message,
      request_data: requestData,
      userAgent: navigator.userAgent,
      url: paramUrl?.href,
      code: err?.code,
      user_id: this.userId,
      err_count: 1,
    };
  }
  
  /**
   * Sends a code error log to Site24x7.
   * This method formats the error details and adds them to the code error queue.
   * If the `#CODE_ERROR_LOG_TOKEN` is not set, the method exits without performing any action.
   *
   * @param {Object} params - The parameters for the code error log.
   * @param {string} params.type - The type of the error (e.g., runtime, syntax).
   * @param {string} params.errorName - The name of the error.
   * @param {string} params.message - The error message.
   * @param {string} params.component - The name of the component where the error occurred.
   * @param {string} params.info - Additional information about the error.
   * @param {object} params.userFlow - Additional information about the error.
   *
   * @return {void}
   */
  sendCodeError({
    type,
    errorName,
    message,
    component,
    info,
    userFlow,
    onSiteTime,
  }) {
    const currentUrl = window.location.href;
    if (!this.#CODE_ERROR_LOG_TOKEN) {
      console.error('UNSENT ERROR:', {
        type, errorName, message, component, info, userFlow, currentUrl,
        user_id: this.userId,
      });
      return;
    }
    
    const key = `${type}:${message}:${component}`;
    
    this.addError({
      date: (new Date()).toISOString(),
      channel: this.#CHANNEL_NAME,
      user_id: this.userId,
      err_count: 1,
      version: this.#BUILD,
      userAgent: navigator.userAgent,
      currentUrl,
      type,
      errorName,
      message,
      component,
      info,
      userFlow,
      onSiteTime,
    }, key, this.#codeErrorQueue);
  }
  
  /**
   * Send a buy flow to Site24x7.
   * @param {array} flowList
   * @param {Event|undefined} event
   * @return {void}
   */
  sendBuyFlow(flowList, event) {
    const request = {
      url: `${this.#url}?token=${this.#BUY_FLOW_LOG_TOKEN}`,
      baseUrl: this.#baseUrl,
      method: API_METHODS.POST,
      params: [{
        userAgent: navigator.userAgent,
        version: this.#BUILD,
        channel: this.#CHANNEL_NAME,
        user_id: this.userId,
        date: (new Date()).toISOString(),
        flow: flowList,
      }],
    };
    if (!this.#BUY_FLOW_LOG_TOKEN || !flowList.length) {
      flowList.length && console.error('UNSENT BUY FLOW:', request);
      return;
    }
    event? this.sendBeacon(request) : this.sendError(request);
  }
  /**
   * Sends an error to Site24x7.
   * This method wraps the request method to catch any errors that occur during the request.
   * If the request fails, the error is logged to the console.
   * @param {object} req - The request object.
   * @param {string} req.url - The URL of the request.
   * @param {string} req.baseUrl - The base URL of the request.
   * @param {string} req.method - The HTTP method of the request.
   * @param {Array} req.params - The parameters of the request.
   * @return {void}
   */
  sendError(req) {
    request(req).catch(console.log);
  }
  
  /**
   * Sends a beacon to Site24x7.
   * This method uses the `navigator.sendBeacon` API to send data to Site24x7
   * when the user is leaving the page.
   * It formats the request parameters into a Blob and sends it to the specified URL.
   * @param {object} req - The request object.
   * @param {string} req.url - The URL of the request.
   * @param {string} req.baseUrl - The base URL of the request.
   * @param {string} req.method - The HTTP method of the request.
   * @param {Array} req.params - The parameters of the request.
   * @return {void}
   */
  sendBeacon(req) {
    const params = req.params.map((item) => {
      item.beforeUnload = true;
      return item;
    });
    
    const url = new URL(req.url, req.baseUrl);
    const blob = new Blob(params, {type: 'application/json'});
    navigator.sendBeacon(url.href, blob);
  }
  
  /**
   * Sends a request error log to Site24x7.
   * If the request error log token is not set or the error originates from the
   * Site24x7 base URL, the method exits.
   * Otherwise, it checks if the error needs to be tracked and, if so,
   * prepares the error parameters,
   * generates a unique key, and adds the error to the request error queue.
   *
   * @param {Object} err - The error object containing details of the request error.
   * @param {Object} err.config - The configuration of the request that caused the error.
   * @param {string} err.config.url - The URL of the request.
   * @param {string} err.config.baseURL - The base URL of the request.
   * @param {Object} [err.config.data] - The data sent with the request.
   * Can be a FormData object, string, or other types.
   * @param {Array} [err.config.customConfig.skipTrackingCodes] - List of status
   * codes to skip tracking.
   * @param {Array} [err.config.customConfig.skipTrackingReg] - List of regex
   * patterns to skip tracking.
   * @param {Array} [err.config.customConfig.trackParams] - List of parameters to track.
   * @param {Object} err.response - The response object of the failed request.
   * @param {number} err.response.status - The HTTP status code of the response.
   * @param {string} [err.response.data.message] - The error message from the response data.
   * @param {string} [err.response.statusText] - The status text of the response.
   * @param {string} err.code - The error code associated with the request failure.
   * @param {string} err.message - The error message.
   *
   * @return {void}
   */
  sendRequestError(err) {
    if (err.config?.baseURL === this.#baseUrl) return;
    const need = this.needTrackingRequest(err);
    if (!this.#REQUEST_ERROR_LOG_TOKEN) {
      need && console.error('UNSENT ERROR:', {...err, user_id: this.userId});
      return;
    }
    if (need) {
      const params = this.prepareParams(err);
      const key = `${params.status}|${params.url}|${params.message}`;
      this.addError(params, key, this.#requestErrorQueue);
    }
  }
  
  
  /**
   * Determines whether a request error needs to be tracked.
   * This method evaluates the error object against a set of conditions to decide
   * if the error should be skipped or tracked.
   *
   * @param {Object} err - The error object containing details of the request error.
   * @param {Object} [err.config] - The configuration of the request that caused the error.
   * @param {Object} [err.config.customConfig] - Custom configuration for tracking.
   * @param {Array<number>} [err.config.customConfig.skipTrackingCodes] - List
   * of status codes to skip tracking.
   * @param {Array<string>} [err.config.customConfig.skipTrackingReg] - List of
   * regex patterns to skip tracking.
   * @param {Object} err.response - The response object of the failed request.
   * @param {number} err.response.status - The HTTP status code of the response.
   * @param {string} err.code - The error code associated with the request failure.
   *
   * @return {boolean} Returns `true` if the error needs to be tracked, `false` otherwise.
   */
  needTrackingRequest(err) {
    const config = err.config?.customConfig;
    const status = err.response?.status;
    const code = err.code;
    
    const hasReasonToSkip = [
      config?.skipTrackingCodes?.includes(status),
      config?.skipTrackingReg?.some((codeReg) => {
        const reg = new RegExp(codeReg);
        return reg.test(String(status));
      }),
      NOT_TRACKED_STATUS_CODES.includes(status),
      NOT_TRACKED_ERROR_CODES.includes(code),
    ];
    
    return !hasReasonToSkip.some((skip) => skip);
  }
}

const siteTracker = new SiteTracker();
export default siteTracker;
window.addEventListener('beforeunload', siteTracker.releaseQueue);
